import { decrypt, decryptWithKey, encryptWithKey } from '@/helpers/encrypt'

const STREAM = 'api/request'

const DB_NAME = 'PROOFOFID'
const TABLE = 'KEYS'
const KEY_NAMES = {
  privateKey: 'PRIVATE_KEY',
  publicKey: 'PUBLIC_KEY'
}

// Custom error types for better error handling
class KeyFetchError extends Error {
  constructor (message) {
    super(message)
    this.name = 'KeyFetchError'
  }
}

class DecryptionError extends Error {
  constructor (message) {
    super(message)
    this.name = 'DecryptionError'
  }
}

class TimeoutError extends Error {
  constructor (message) {
    super(message)
    this.name = 'TimeoutError'
  }
}

class NetworkError extends Error {
  constructor (message) {
    super(message)
    this.name = 'NetworkError'
  }
}

class DeliveryError extends Error {
  constructor (message) {
    super(message)
    this.name = 'DeliveryError'
  }
}

export default {
  namespaced: true,
  state: {
    items: [],
    requests: []
  },
  getters: {},
  mutations: {
    setNewRequests (state, newRequests) {
      state.requests = newRequests
    }
  },
  actions: {
    async fetchRequestById ({ dispatch }, reqId) {
      try {
        const result = await dispatch('fetchDataWithURL', { stream: 'api/request', params: reqId }, { root: true })

        // Check if the result contains an error
        if (result && result.error) {
          return null // Return null instead of throwing
        }

        return result
      } catch (error) {
        return null // Return null to allow component to handle gracefully
      }
    },
    async fetchRequestByShortKey ({ dispatch }, shortKey) {
      try {
        const result = await dispatch('fetchDataWithURL', { stream: 'api/request/short-key', params: shortKey }, { root: true })

        // Check if the result contains an error
        if (result && result.error) {
          return null
        }

        return result
      } catch (error) {
        return null
      }
    },
    async fetchRequestValidity ({ dispatch }, reqId) {
      try {
        const result = await dispatch('fetchDataWithURL', { stream: 'api/request/validate', params: reqId }, { root: true })

        // Check if the result contains an error
        if (result && result.error) {
          return { isActive: false, error: result.error }
        }

        return result
      } catch (error) {
        return { isActive: false, error: error.message }
      }
    },
    async fetchRequestsByDate ({ rootState, dispatch }, { startDate, endDate }) {
      return await dispatch('fetchDataWithParams', { stream: 'api/request/metrics/by-requestor', params: { requestor_id: rootState.auth.authId, start_date: startDate, end_date: endDate } }, { root: true })
    },
    async fetchRequestsByRequestorID ({ rootState, dispatch }, { page, limit, order }) {
      try {
        const result = await dispatch('fetchDataWithParams', {
          stream: 'api/request/list/by-requestor',
          params: {
            requestor_id: rootState.auth.authId,
            page,
            limit,
            order
          }
        }, { root: true })

        // Check if the result contains an error
        if (result && result.error) {
          return { requests: [], total: 0, error: result.error }
        }

        return result || { requests: [], total: 0 }
      } catch (error) {
        return { requests: [], total: 0, error: error.message }
      }
    },
    async createRequest ({ rootState, dispatch }) {
      const request = {
        responderId: rootState.auth.authId,
        deviceId: localStorage.getItem('DEVICE_ID')
      }
      return await dispatch('createData', { stream: STREAM + '/create', body: request }, { root: true })
    },
    async startNotificationListener ({ rootState, dispatch }, { callbackError, callbackRequest, callbackResponse, callbackWebsocketClosed }) {
      const client = rootState.auth.authId
      if (client) {
        return await dispatch('startWebSocket', {
          callbackError,
          callbackRequest,
          callbackResponse,
          callbackWebsocketClosed,
          deviceID: localStorage.getItem('DEVICE_ID'),
          clientID: client
        }, { root: true })
      }
    },
    async sendNotification ({ rootState, dispatch }, { requestID, geoLocation }) {
      return await dispatch('sendNotificationRequest', { requestID, clientID: rootState.auth.authId, geoLocation }, { root: true })
    },
    async sendDecision ({ rootState, dispatch }, { request, approval }) {
      return await dispatch('sendNotificationDecision', { requestID: request.id, clientID: rootState.auth.authId, approval }, { root: true })
    },
    async transferData ({ dispatch }, { publicKeyID, requestId }) {
      const key = await dispatch('getPublicKey', publicKeyID, { root: true })
      const documentsRaw = await dispatch('documentsLs/getItems', null, { root: true })
      const ecryptedData = await encryptWithKey(documentsRaw, key.publicKey)
      const delivery = {
        requestId,
        deliveryData: ecryptedData
      }
      return await dispatch('createData', { stream: 'api/delivery/create', body: delivery }, { root: true })
    },
    async decryptDataTransferred ({ rootState, dispatch }, requestId) {
      try {
        // Input validation - check if requestId is valid
        if (!requestId || typeof requestId !== 'string' || requestId.trim() === '') {
          throw new Error('Invalid request ID - must be a non-empty string')
        }

        // Fetch encryption keys with timeout
        const keyFetchPromise = Promise.all([
          dispatch('getByUserId', { database: DB_NAME, table: TABLE, id: KEY_NAMES.privateKey, userId: rootState.auth.authId }, { root: true }),
          dispatch('getByUserId', { database: DB_NAME, table: TABLE, id: KEY_NAMES.publicKey, userId: rootState.auth.authId }, { root: true })
        ])

        const [privateEncryptedKey, publicEncryptedKey] = await Promise.race([
          keyFetchPromise,
          new Promise((_resolve, reject) => setTimeout(() => reject(new TimeoutError('Key fetch timeout')), 10000))
        ])

        if (!privateEncryptedKey || !publicEncryptedKey) {
          throw new KeyFetchError('Encryption keys not found')
        }

        const privateKey = await decrypt(rootState, privateEncryptedKey.data)
        const publicKey = await decrypt(rootState, publicEncryptedKey.data)

        if (!privateKey || !publicKey) {
          throw new DecryptionError('Failed to decrypt encryption keys')
        }

        // Fetch delivery data with timeout
        const delivery = await Promise.race([
          dispatch('fetchDataWithURL', { stream: 'api/delivery', params: requestId }, { root: true }),
          new Promise((_resolve, reject) => setTimeout(() => reject(new TimeoutError('Delivery fetch timeout')), 15000))
        ])

        // Check for API errors or missing data
        if (!delivery) {
          throw new DeliveryError('No delivery data available for this request')
        }

        // Check for "no delivery found" message from backend - this means no delivery record exists yet
        if (delivery.message && delivery.message.toLowerCase().includes('no delivery found')) {
          return null // Return null to trigger retry logic
        }

        // Check for explicit error messages from API
        if (delivery.error || delivery.message === 'Request not found' || delivery.message === 'No data available') {
          throw new DeliveryError(delivery.error || delivery.message || 'Request data not available')
        }

        // Check for pending data (data not yet available but might be later)
        if (delivery.message && delivery.message.includes('pending')) {
          return null
        }

        // Check if deliveryData field is missing or empty - return null for retry
        if (!delivery.deliveryData || delivery.deliveryData === '' || delivery.deliveryData === null || delivery.deliveryData === undefined) {
          return null // Return null to trigger retry instead of 'expired'
        }

        // Decrypt the data with timeout
        const decryptedData = await Promise.race([
          decryptWithKey(rootState.auth.authId, delivery.deliveryData, privateKey, publicKey),
          new Promise((_resolve, reject) => setTimeout(() => reject(new TimeoutError('Decryption timeout')), 20000))
        ])

        return decryptedData
      } catch (error) {
        // Return appropriate error indicators based on error type using instanceof
        if (error instanceof KeyFetchError || error instanceof DecryptionError || error instanceof TimeoutError) {
          return 'expired' // Indicates key issues or timeouts
        }

        if (error instanceof NetworkError || error instanceof DeliveryError) {
          return null // Indicates network issues - trigger retry
        }

        // For other errors, return null to trigger retry logic
        return null
      }
    }
  }
}
// Force reload
